import Duck from 'extensible-duck';
import Immutable from 'immutable';
import { normalize, schema, denormalize } from 'normalizr';
import { arrayMove } from 'react-sortable-hoc';
import { createSelector } from 'reselect';

const downloadSchema = new schema.Entity('$$downloads');

import requestsManager from 'lib/requestsManagerAxios'; // eslint-disable-line

export default new Duck({
  namespace: 'organization-config',
  store: 'downloads',
  types: [
    'INITIALIZE',
    'INITIALIZE_PENDING',
    'INITIALIZE_FULFILLED',
    'INITIALIZE_REJECTED',
    'CLOSE_SNACKBAR',
    'CREATE_DOWNLOAD',
    'CREATE_DOWNLOAD_PENDING',
    'CREATE_DOWNLOAD_FULFILLED',
    'CREATE_DOWNLOAD_REJECTED',
    'DELETE_DOWNLOAD',
    'DELETE_DOWNLOAD_PENDING',
    'DELETE_DOWNLOAD_FULFILLED',
    'DELETE_DOWNLOAD_REJECTED',
    'MOVE_DOWNLOAD',
    'MOVE_DOWNLOAD_PENDING',
    'MOVE_DOWNLOAD_FULFILLED',
    'MOVE_DOWNLOAD_REJECTED',
    'UPDATE_DOWNLOAD',
    'UPDATE_DOWNLOAD_PENDING',
    'UPDATE_DOWNLOAD_FULFILLED',
    'UPDATE_DOWNLOAD_REJECTED',
  ],
  initialState: Immutable.Map({
    devToolsOn: true,
    setIsFetching: false,
    setIsSaving: false,
    snackbarMessage: '',
    snackbarOpen: false,
    $$entities: Immutable.Map({
      $$downloads: Immutable.Map(),
    }),
    $$downloadIds: Immutable.OrderedSet(),
  }),
  reducer: ($$state, { type, payload, meta }, { types }) => {
    switch (type) {
      case types.CLOSE_SNACKBAR:
        return $$state.merge({ snackbarMessage: '', snackbarOpen: false });
      case types.CREATE_DOWNLOAD_FULFILLED: {
        const normalizedDownload = normalize(payload.data, downloadSchema);
        return $$state
          .update('$$downloadIds', $$downloadIds =>
            $$downloadIds.add(normalizedDownload.result)
          )
          .mergeDeep({
            $$entities: normalizedDownload.entities,
            setIsSaving: false,
          });
      }
      case types.CREATE_DOWNLOAD_REJECTED:
        return $$state.merge({ setIsSaving: false });
      case types.CREATE_DOWNLOAD_PENDING:
      case types.DELETE_DOWNLOAD_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.DELETE_DOWNLOAD_FULFILLED:
        return $$state
          .update('$$downloadIds', $$downloadIds =>
            $$downloadIds.filterNot(downloadId => downloadId === meta.id)
          )
          .merge({
            setIsSaving: false,
            snackbarOpen: true,
            snackbarMessage: 'Download deleted.',
          });
      case types.DELETE_DOWNLOAD_REJECTED:
        return $$state.merge({
          setIsSaving: false,
          snackbarOpen: true,
          snackbarMessage:
            'There was a problem deleting this download. Try refreshing the page',
        });
      case types.INITIALIZE_PENDING:
        return $$state.merge({ setIsFetching: true });
      case types.INITIALIZE_FULFILLED: {
        const normalizedDownloads = normalize(payload.data, [downloadSchema]);
        return $$state.mergeDeep({
          $$entities: normalizedDownloads.entities,
          $$downloadIds: Immutable.OrderedSet(normalizedDownloads.result),
          setIsFetching: false,
        });
      }
      case types.INITIALIZE_REJECTED:
        return $$state.merge({
          snackbarMessage:
            'Issue loading this page. Please refresh and try again.',
          snackbarOpen: true,
          setIsFetching: false,
        });
      case types.MOVE_DOWNLOAD_PENDING: {
        const newDownloadIds = arrayMove(
          $$state.get('$$downloadIds').toJS(),
          meta.oldIndex,
          meta.newIndex
        );
        return $$state.merge({
          setIsSaving: true,
          $$downloadIds: Immutable.OrderedSet(newDownloadIds),
        });
      }
      case types.MOVE_DOWNLOAD_FULFILLED: {
        const normalizedDownloads = normalize(payload.data, [downloadSchema]);
        return $$state.mergeDeep({
          $$entities: normalizedDownloads.entities,
          $$downloadIds: Immutable.OrderedSet(normalizedDownloads.result),
          setIsSaving: false,
        });
      }
      case types.MOVE_DOWNLOAD_REJECTED: {
        const oldDownloadIds = arrayMove(
          $$state.get('$$downloadIds').toJS(),
          meta.newIndex,
          meta.oldIndex
        );
        return $$state.merge({
          snackbarMessage:
            'Issue moving this Download. Please refresh and try again.',
          snackbarOpen: true,
          setIsSaving: false,
          $$downloadIds: Immutable.OrderedSet(oldDownloadIds),
        });
      }
      case types.UPDATE_DOWNLOAD_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.UPDATE_DOWNLOAD_FULFILLED: {
        const normalizedDownload = normalize(payload.data, downloadSchema);
        return $$state.mergeDeep({
          $$entities: normalizedDownload.entities,
          setIsSaving: false,
          snackbarOpen: true,
          snackbarMessage: 'Download has been updated',
        });
      }
      case types.UPDATE_DOWNLOAD_REJECTED:
        return $$state.merge({
          setIsSaving: false,
          snackbarOpen: !payload.response.data,
          snackbarMessage:
            !payload.response.data &&
            "We're having trouble updating this. Try refreshing the page.",
        });
      default:
        return $$state;
    }
  },
  selectors: {
    setIsFetching: $$state => $$state.get('setIsFetching'),
    setIsSaving: $$state => $$state.get('setIsSaving'),
    snackbarMessage: $$state => $$state.get('snackbarMessage'),
    snackbarOpen: $$state => $$state.get('snackbarOpen'),
    downloads: new Duck.Selector(_selectors =>
      createSelector(
        $$state => $$state.get('$$downloadIds'),
        $$state => $$state.getIn(['$$entities', '$$downloads']),
        ($$downloadIds, $$downloads) =>
          denormalize($$downloadIds.toJS(), [downloadSchema], {
            $$downloads: $$downloads.toJS(),
          })
      )
    ),
  },
  creators: ({ types }) => ({
    closeSnackbar: () => ({ type: types.CLOSE_SNACKBAR }),
    createDownload: (title, description, file) => {
      const data = new FormData();
      data.append('download[title]', title);
      data.append('download[description]', description);
      data.append('download[s3_attachment]', file);
      return {
        type: types.CREATE_DOWNLOAD,
        payload: requestsManager.post('/admin/downloads', data),
      };
    },
    deleteDownload: id => ({
      type: types.DELETE_DOWNLOAD,
      payload: requestsManager.delete(`/admin/downloads/${id}`),
      meta: { id },
    }),
    initialize: () => ({
      type: types.INITIALIZE,
      payload: requestsManager.get('/admin/downloads.json'),
    }),
    moveDownload: (id, oldIndex, newIndex) => ({
      type: types.MOVE_DOWNLOAD,
      payload: requestsManager.post(`/admin/downloads/${id}/move`, {
        position: newIndex + 1,
      }),
      meta: { oldIndex, newIndex },
    }),
    updateDownload: (id, title, description) => ({
      type: types.UPDATE_DOWNLOAD,
      payload: requestsManager.patch(`/admin/downloads/${id}`, {
        download: { title, description },
      }),
    }),
  }),
});
